import React from "react";

import { Table } from "../../UI/Table";
import { TableRow } from "../../UI/TableRow";
import { TableHead } from "../../UI/TableHead";
import { TableCell } from "../../UI/TableCell";
import { EnhancedTableBody } from "../EnhancedTableBody";
import { useUserContext } from "../../contexts/UserContext";
import { UserNameLink } from "../UserNameLink";
import { RoommateLockSwitch } from "./RoommateLockSwitch";
import { ModalProvider } from "../../contexts/ModalContext";
import { BadroomSwitch } from "./BedroomSwitch";
import { useNetwork } from "../../hooks/useNetwork";
import { normalize } from "../../helpers/normalize";
import { HelpTooltip } from "../HelpTooltip";
import { Typography } from "../../UI/Typography";
import { dateToMMDDYY, getDate } from "../../common/date_utils";

const MATCHING_SOURCE_TOOLTIP_TEXT =
  "This column displays how these roommates were matched together. Options include being matched by a property administrator, by the user self-selecting or by data import";

const CREATION_DATE_TOOLTIP_TEXT =
  "On this date, the two users became roommates. You can also view the earliest creation dates among all users by downloading the Roommate Report under Reports/Roommate Report. ";

const LOCK_IN_MATCH_TOOLTIP_TEXT =
  "By locking in the match on a row, the two users will be unable to leave the group. More users can still join the group if it is not full. If you have questions about how this tool works, please call us at (352) 327 - 4061, Option 2 or e-mail support@roomsync.com.";  

export enum RoommateType {
  CURRENT,
  PAST
}
interface RoommatesTableProps {
  roommateType: RoommateType;
}

export const RoommatesTable: React.FC<RoommatesTableProps> = ({roommateType}) => {
  const { user, isLoading } = useUserContext();
  const { getL3, getL2 } = useNetwork();

  const l3 = React.useMemo(() => {
    if (!user) return;

    return getL3(user?.l1Id, user?.l2Id, user?.l3Id);
  }, [normalize(user)]);

  const l2 = React.useMemo(() => {
    if (!user) return;

    return getL2(user?.l1Id, user?.l2Id);
  }, [normalize(user), getL2]);

  let roommates = [];

  switch (roommateType) {
    case RoommateType.CURRENT:
      roommates = user?.roommates || [];
      break;
    case RoommateType.PAST:
      roommates = user?.pastRoommates || [];
      break;
  }


  const getRoomMateSource = (source: string) => {
    if(source === "IMPORT") {
      return "IMPORTED";
    }
    if(source === "USER") {
      return "SELF-SELECTED";
    }
    if(source === "ADMIN") {
      return "ADMINISTRATOR";
    }
    return "";
  };

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Roommate</TableCell>
          {
            (!!l3?.inSameRoomEnabled && roommateType === RoommateType.CURRENT)
              && <TableCell>In same room</TableCell>
          }
          <TableCell>
            <HelpTooltip
              title="Matching Source"
              text={MATCHING_SOURCE_TOOLTIP_TEXT}
            >
              Matching Source
            </HelpTooltip>
          </TableCell>
          <TableCell>
            <HelpTooltip
              title="Matching Source"
              text={CREATION_DATE_TOOLTIP_TEXT}
            >
              Creation Date
            </HelpTooltip>
          </TableCell>
          {
            roommateType === RoommateType.CURRENT &&
            <HelpTooltip
              title="Matching Source"
              text={LOCK_IN_MATCH_TOOLTIP_TEXT}
              withIconAndChild
            >
            <TableCell>Lock in match</TableCell>
          </HelpTooltip>
          }
          
          {l2?.previouslyKnownEnabled && (
            <TableCell>Known Before RoomSync</TableCell>
          )}
        </TableRow>
      </TableHead>
      <EnhancedTableBody colSpan={4} isLoading={isLoading}>
        {roommates.map((roommate) => (
          <TableRow key={roommate.membershipId}>
            <TableCell>
              <UserNameLink
                id={roommate.membershipId}
                name={`${roommate.firstName} ${roommate.lastName}`}
              />
            </TableCell>
            {(l3?.inSameRoomEnabled && roommateType === RoommateType.CURRENT) && (
              <TableCell>
                <BadroomSwitch
                  roommateId={roommate.membershipId}
                  inSameRoom={roommate.inSameRoom}
                />
              </TableCell>
            )}
            <TableCell>
              <Typography variant="body2">{getRoomMateSource(roommate.source)}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{dateToMMDDYY(getDate(roommate.matchCreationTime))}</Typography>
            </TableCell>
            {
            roommateType === RoommateType.CURRENT &&
              <TableCell>
                <ModalProvider>
                  <RoommateLockSwitch
                    adminLocked={roommate.adminLocked}
                    roommateId={roommate.membershipId}
                  />
                </ModalProvider>
              </TableCell>
            }
            {l2?.previouslyKnownEnabled && (
              <TableCell>{roommate.previouslyKnown ? "Yes" : "No"}</TableCell>
            )}
          </TableRow>
        ))}
      </EnhancedTableBody>
    </Table>
  );
};
